// @flow
import * as React from 'react'
import is from 'mz-utils/is'
import { isMobileBrowser } from '../../utils'
import AnimatedPortal from '../AnimatedPortal'
import DesktopLightbox from './DesktopLightbox'
import MobileScreen from './MobileScreen'
import VisibilityController from './VisibilityController'
import { Container } from './elements'

// Types
export type Props = {
  id?: string,
  render: (props: {
    showed: boolean,
    close: Function,
    appearance: Appearance,
  }) => any,
  controllerRef?: (controller: ?VisibilityController) => any,
  appearance?: ?Appearance,
  onClose?: Function,
  closable?: boolean,
  showed?: boolean,
  title?: AppearanceTitle,
  subtitle?: AppearanceTitle,
  enableAnimation?: boolean,
}

export type Appearance = 'mobile' | 'desktop'

export type AppearanceTitle =
  | React.Node
  | {
      mobile?: React.Node,
      desktop?: React.Node,
    }

// Utils
const APPEARANCE_COMPONENT = {
  mobile: MobileScreen,
  desktop: DesktopLightbox,
}

const getAppearanceTitle = (
  title: ?AppearanceTitle,
  appearance: Appearance
): React.Node => {
  if (!title) return ''
  if (React.isValidElement(title)) return (title: any)
  if (is.object(title)) return (title: any)[appearance] || ''
  return (title: any)
}

const getActualAppearance = (propAppearance: ?Appearance) => {
  if (propAppearance) {
    return propAppearance === 'mobile' ? 'mobile' : 'desktop'
  }
  return isMobileBrowser() ? 'mobile' : 'desktop'
}

/**
 * Adaptive lightbox that shows children in DekstopLightbox on desktops
 * and in MobileScreen on mobile devices.
 * @param  {Object} props
 * @return {React.Element}
 */
const Lightbox = ({
  id,
  showed,
  onClose,
  render,
  controllerRef,
  title,
  subtitle,
  closable,
  ...props
}: Props) => (
  <VisibilityController
    id={id}
    ref={controllerRef}
    showed={showed}
    onClose={onClose}
    render={(rawShowed, close) => (
      <AnimatedPortal
        showed={rawShowed}
        duration={650}
        render={actualShowed => {
          const actualAppearance = getActualAppearance(props.appearance)
          const children = render({
            showed: actualShowed,
            appearance: actualAppearance,
            close,
          })

          const closeHandler = closable ? close : null
          const ViewComponent = APPEARANCE_COMPONENT[actualAppearance]
          const appearanceTitle = getAppearanceTitle(title, actualAppearance)
          const appearanceSubtitle = getAppearanceTitle(
            subtitle,
            actualAppearance
          )

          return (
            <Container $showed={actualShowed}>
              <ViewComponent
                {...props}
                onClose={closeHandler}
                showed={actualShowed}
                title={appearanceTitle}
                subtitle={appearanceSubtitle}
              >
                {children}
              </ViewComponent>
            </Container>
          )
        }}
      />
    )}
  />
)
Lightbox.defaultProps = {
  closable: true,
  enableAnimation: true,
}

export default Lightbox
